import { aboutData, stats } from "@/data/mock";
import { motion } from "framer-motion";
import { Target, Eye, Lightbulb, Award, Handshake, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6 }
};

const valueIcons = [Lightbulb, Award, Handshake, ShieldCheck];

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-onix-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.08),transparent_70%)]" />
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(rgba(6,182,212,0.05) 1px, transparent 1px)",
            backgroundSize: "50px 50px"
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Conócenos</span>
            <h1 className="text-4xl md:text-5xl font-orbitron font-bold text-white mt-4 mb-6">
              Acerca de <span className="text-cyan-400">Grupo Onix</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
              Más de 8 años llevando la tecnología al alcance de tus manos con soluciones profesionales en redes, seguridad y energía solar.
            </p>
          </motion.div>
        </div>
      </section>

      {/* History */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Nuestra Historia</span>
              <h2 className="text-3xl font-orbitron font-bold text-white mt-3 mb-6">
                De la pasión a la <span className="text-cyan-400">excelencia</span>
              </h2>
              <p className="text-slate-400 leading-relaxed text-base">
                {aboutData.history}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="p-6 rounded-2xl bg-onix-card border border-white/5 text-center hover:border-cyan-500/20 transition-all duration-300"
                >
                  <div className="text-3xl font-orbitron font-bold text-cyan-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-onix-darker">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              {...fadeUp}
              className="p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/20 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                <Target size={28} className="text-cyan-400" />
              </div>
              <h3 className="text-2xl font-orbitron font-bold text-white mb-4">Nuestra Misión</h3>
              <p className="text-slate-400 leading-relaxed">
                {aboutData.mission}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/20 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                <Eye size={28} className="text-cyan-400" />
              </div>
              <h3 className="text-2xl font-orbitron font-bold text-white mb-4">Nuestra Visión</h3>
              <p className="text-slate-400 leading-relaxed">
                {aboutData.vision}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Lo que nos define</span>
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold text-white mt-3">
              Nuestros <span className="text-cyan-400">Valores</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutData.values.map((val, i) => {
              const Icon = valueIcons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-onix-card border border-white/5 hover:border-cyan-500/20 transition-all duration-300 text-center group"
                >
                  <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-5 group-hover:bg-cyan-500/20 transition-colors">
                    <Icon size={24} className="text-cyan-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{val.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{val.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-onix-darker">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-orbitron font-bold text-white mb-6">
              ¿Quieres trabajar con nosotros?
            </h2>
            <p className="text-slate-400 mb-10 max-w-xl mx-auto">
              Estamos listos para llevar tu proyecto al siguiente nivel. Contáctanos y descubre cómo podemos ayudarte.
            </p>
            <Link
              to="/contacto"
              className="group inline-flex items-center gap-2 px-10 py-4 bg-cyan-500 text-onix-dark font-bold rounded-xl hover:bg-cyan-400 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
            >
              Contáctanos
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
